<template>
  <div class="store-select">
    <!-- 门店列表 -->
    <base-list-management ref="baseListRef" :configData="configData">
      <!-- 在搜索框右侧添加新建门店链接 -->
      <template slot="custom-export">
        <div class="add-store-link">
          <span class="no-suitable-text">没有合适的门店，</span>
          <el-link type="primary" :underline="false" @click="handleAddStore">
            点击新建
          </el-link>
        </div>
      </template>
    </base-list-management>
  </div>
</template>

<script setup>
import { storeList } from '@/api/domain/shop'
import { computed, nextTick, ref, watch } from 'vue'
import { storeSelectTableConfig } from '../config/storeSelectTable'
import { useBatchSession } from '../hooks/useBatchSession'
import {
  openAddStorePage,
  useStoreAddedListener,
} from '../hooks/useStoreManagement'

const emit = defineEmits(['change'])

const { storeSelectedIds, setStoreSelectedIds } = useBatchSession()

const baseListRef = ref(null)

// 从 Vuex 读取已选门店ID列表（保持响应式）
const selectedStoreIds = computed({
  get: () => storeSelectedIds.value || [],
  set: (val) => setStoreSelectedIds(val),
})

// 标志位：是否正在恢复选中状态（防止 toggleRowSelection 触发 handleSelectionChange）
const isRestoring = ref(false)

// 处理选择变化（单选、全选、取消全选都会触发）
const handleSelectionChange = (selection) => {
  // 如果正在恢复选中状态，忽略此次事件（避免被覆盖）
  if (isRestoring.value) {
    return
  }

  // 获取当前页的数据
  const currentPageList = baseListRef.value?.$refs?.baseTable?.list || []
  const currentPageIds = currentPageList.map((row) => row.id)

  // 获取当前页选中的 ID（从 selection 中筛选出属于当前页的）
  // 注意：selection 包含所有已渲染页面的选中行，需要过滤出当前页的
  const currentPageSelectedIds = selection
    .filter((row) => currentPageIds.includes(row.id))
    .map((row) => row.id)

  // 移除当前页的所有 ID（无论之前是否选中）
  selectedStoreIds.value = selectedStoreIds.value.filter(
    (id) => !currentPageIds.includes(id),
  )

  // 添加当前页新选中的 ID
  selectedStoreIds.value.push(...currentPageSelectedIds)

  // 通知父组件
  emitChange()
}

// 恢复当前页的选中状态
// 当表格数据变化时（翻页），根据 selectedStoreIds 自动恢复选中状态
const restoreCurrentPageSelection = () => {
  // 等待下一帧确保 Element UI 表格完全渲染
  nextTick(() => {
    const tableInstance =
      baseListRef.value?.$refs?.baseTable?.$refs?.blmCommonTable?.$refs?.table
    const list = baseListRef.value?.$refs?.baseTable?.list || []

    if (!tableInstance || list.length === 0) return

    const selectedIdsSet = new Set(selectedStoreIds.value)

    // 设置标志位，防止 toggleRowSelection 触发的 selection-change 事件修改 selectedStoreIds
    isRestoring.value = true

    // 对当前页的每一行，检查是否应该被选中
    list.forEach((row) => {
      if (selectedIdsSet.has(row.id)) {
        tableInstance.toggleRowSelection(row, true)
      }
    })

    // 延迟重置标志位，确保所有 selection-change 事件都被忽略
    nextTick(() => {
      isRestoring.value = false
    })
  })
}

// 监听表格数据变化（翻页时），自动恢复选中状态
// 只监听第一行的 ID，避免 deep 监听整个数组
watch(
  () => baseListRef.value?.$refs?.baseTable?.list?.[0]?.id,
  (newFirstId) => {
    if (newFirstId !== undefined) {
      // flush: 'post' 确保在 DOM 更新后执行
      restoreCurrentPageSelection()
    }
  },
  { flush: 'post' },
)

// 生成配置数据
const configData = ref(
  storeSelectTableConfig({
    apiFn: storeList,
    handleSelectionChange,
  }),
)

// 通知父组件选择变化
const emitChange = () => {
  emit('change', selectedStoreIds.value)
}

// 是否有选中
const hasSelected = () => {
  return selectedStoreIds.value.length > 0
}

// 获取数据（返回已选门店ID列表）
const getData = () => {
  return selectedStoreIds.value
}

// 刷新门店列表
const refresh = () => {
  baseListRef.value?.searchForm()
}

// 新建门店
const handleAddStore = () => {
  openAddStorePage()
}

// 监听门店新建成功，刷新列表并自动勾选
useStoreAddedListener((newStoreId) => {
  // 1. 如果传递了新建门店的 ID，先添加到已选列表
  if (newStoreId && !selectedStoreIds.value.includes(newStoreId)) {
    selectedStoreIds.value = [...selectedStoreIds.value, newStoreId]
  }

  // 2. 延迟刷新门店列表（等待后端数据同步）
  // 刷新后，现有的 watch 机制会自动恢复选中状态
  nextTick(() => {
    baseListRef.value?.searchForm()
  })
})

// 监听 Vuex 中的 storeSelectedIds 变化，恢复表格选中状态
watch(
  selectedStoreIds,
  (newIds) => {
    if (newIds?.length > 0) {
      // 延迟执行，确保表格已经加载完成
      nextTick(() => {
        restoreCurrentPageSelection()
        emitChange()
      })
    }
  },
  { immediate: true },
)

defineExpose({
  hasSelected,
  getData,
  refresh,
})
</script>

<style lang="scss" scoped>
.store-select {
  // 让包含自定义链接的 el-form-item 占据剩余空间并右对齐
  :deep(.searchLayoutTop) {
    .el-form-item:last-child {
      flex: 1;
      justify-content: flex-end;
      margin-right: 0;
    }
  }

  :deep(.add-store-link) {
    white-space: nowrap;
  }

  :deep(.no-suitable-text) {
    color: #606266;
    margin-right: 4px;
  }
}
</style>
